import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Button, CircularProgress, MenuItem, Select, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import Chip from '@mui/material/Chip'

import {
  useCreateKybMonitoringMutation,
  useFindKybMonitoringQuery,
  useFindUserMasterQuery,
  useUpdateKybMonitoringMutation,
} from '../../../../services/guenoApi'

import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import toaster from '../../../../toaster'
import { getOptionLevels, getOptionsStatus, getOptionsUnits } from '../../constants'
import FiltersNotificationsMonitoring from './FiltersNotificationsMonitoring'

const KybNotificationsMonitoring = () => {
  const { t } = useTranslation(['common', 'messages'])
  const PERSON_ATTRIBUTES = [
    { label: t('messages:kybNotificationsMonitoring.basicDataPerson'), value: 'basic-data-person' },
    { label: t('messages:kybNotificationsMonitoring.financialDataPerson'), value: 'financial-data-person' },
    { label: t('messages:kybNotificationsMonitoring.addressesExtendedPerson'), value: 'addresses-extended-person' },
    { label: t('messages:kybNotificationsMonitoring.emailsExtendedPerson'), value: 'emails-extended-person' },
    { label: t('messages:kybNotificationsMonitoring.phonesExtendedPerson'), value: 'phones-extended-person' },
    { label: t('messages:kybNotificationsMonitoring.relatedCompanies'), value: 'related-companies' },
    { label: t('messages:kybNotificationsMonitoring.relatedPersons'), value: 'related-persons' }, 
    { label: t('messages:kybNotificationsMonitoring.kycPersona'), value: 'kyc-person' },
    { label: t('messages:kybNotificationsMonitoring.registrationStatus'), value: 'registration-status' }, 
    { label: t('messages:kybNotificationsMonitoring.negativeMediaPerson'), value: 'negative-media-person' },
  ]

  const BUSINESS_ATTRIBUTES = [
    // { label: t('messages:kybNotificationsMonitoring.basicBusinessData'), value: 'basic-data-business' },
    { label: t('messages:kybNotificationsMonitoring.shareholders'), value: 'shareholders' },
    { label: t('messages:kybNotificationsMonitoring.checkKycBusiness'), value: 'kyc-company' },
  ]

  const { emailClient } = useSelector((state) => state.login)
  const [businessTypeFilter, setBusinessTypeFilter] = useState(undefined) // Tipo de filtro específico para business
  const [personTypeFilter, setPersonTypeFilter] = useState(undefined) // Tipo de filtro específico para person
  const [entityType, setEntityType] = useState('business')
  const [allPersonData, setAllPersonData] = useState(false)
  const [allBusinessData, setAllBusinessData] = useState(false) // eslint-disable-line
  const [businessAttributes, setBusinessAttributes] = useState([]) // Estado para manejar los atributos de BUSINESS
  const [personAttributesState, setPersonAttributesState] = useState([]) // Estado para manejar los atributos de PERSON

  // Estados para mantener los datos persistentes de ambas entidades
  const [businessData, setBusinessData] = useState({
    typeFilter: '',
    criteria: [],
    attributes: [],
  })
  const [personData, setPersonData] = useState({
    typeFilter: '',
    criteria: [],
    attributes: [],
  })

  const activeFilters = {
    RISK_LEVEL: 'riskLevel',
    CLIENT_TYPE: 'clientType',
    FILE_STATUS: 'fileStatus',
  }
  const optionLevels = getOptionLevels(t)
  const optionsUnits = getOptionsUnits(t)
  const optionsStatus = getOptionsStatus(t)

  const { data: userMaster, isLoading: loadingUserMaster } = useFindUserMasterQuery({ emailClient })
  const {
    data: kybNotificationsMonitoring,
    isLoading: loadingNotificationsMonitoring,
    refetch,
  } = useFindKybMonitoringQuery()
  const [updateKybNotificationsMonitoring, { isLoading: updatingNotificationsMonitoring }] =
    useUpdateKybMonitoringMutation()
  const [createKybNotificationsMonitoring] = useCreateKybMonitoringMutation()

  const optionsClientTypes = useMemo(
    () =>
      userMaster?.clientTypes?.map((type) => ({
        value: type,
        label: type.toUpperCase(),
      })) || [],
    [userMaster],
  )

  const schema = yup.object().shape({
    filters: yup.array().of(
      yup.object().shape({
        activeFilter: yup.string().required(),
        criteria: yup.array().of(
          yup.object().shape({
            riskLevel: yup.string().notRequired(),
            clientType: yup.string().notRequired(),
            unit: yup.string().required(),
            amount: yup.number().required().min(1),
          }),
        ),
        notifications: yup.array().of(yup.string()).notRequired(),
      }),
    ),
  })

  const defaultValues = useMemo(() => {
    return {
      filters: [
        {
          activeFilter: '',
          criteria: [],
          notifications: [],
        },
      ],
    }
  }, [])

  const { control, handleSubmit, watch, setValue, reset } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues,
  })

  let filters = watch('filters')

  // Función para obtener el tipo de filtro actual según la entidad
  const getCurrentTypeFilter = useCallback(() => {
    return entityType === 'business' ? businessTypeFilter : personTypeFilter
  }, [entityType, businessTypeFilter, personTypeFilter])

  // Función para establecer el tipo de filtro según la entidad
  const setCurrentTypeFilter = useCallback(
    (newType) => {
      if (entityType === 'business') {
        setBusinessTypeFilter(newType)
        setBusinessData((prev) => ({ ...prev, typeFilter: newType }))
      } else {
        setPersonTypeFilter(newType)
        setPersonData((prev) => ({ ...prev, typeFilter: newType }))
      }
    },
    [entityType, setBusinessData, setPersonData],
  )

  const { update: _update } = useFieldArray({
    control,
    name: 'filters',
  })

  const handleAddLevel = (level, filterIndex) => {
    const currentFilters = watch('filters')
    const filter = currentFilters[filterIndex]

    const newCriterion = {
      unit: 'MONTHS',
      amount: 1,
    }

    // Asignar el valor correcto según el tipo de filtro
    if (getCurrentTypeFilter() === 'RISK_LEVEL') {
      newCriterion.riskLevel = level
    } else if (getCurrentTypeFilter() === 'CLIENT_TYPE') {
      newCriterion.clientType = level
    } else if (getCurrentTypeFilter() === 'FILE_STATUS') {
      newCriterion.fileStatus = level
    }

    const newCriteria = [...(filter.criteria || []), newCriterion]

    setValue(`filters.${filterIndex}.criteria`, newCriteria, { shouldDirty: true })

    // Actualizar los datos persistentes inmediatamente
    if (entityType === 'business') {
      setBusinessData((prev) => ({ ...prev, criteria: newCriteria }))
    } else {
      setPersonData((prev) => ({ ...prev, criteria: newCriteria }))
    }
  }

  const handleRemoveCriterion = (filterIndex, criterionIndex) => {
    const currentFilters = watch('filters')
    const updatedCriteria = currentFilters[filterIndex].criteria.filter((_, i) => i !== criterionIndex)
    setValue(`filters.${filterIndex}.criteria`, updatedCriteria, { shouldDirty: true })

    // Actualizar los datos persistentes inmediatamente
    if (entityType === 'business') {
      setBusinessData((prev) => ({ ...prev, criteria: updatedCriteria }))
    } else {
      setPersonData((prev) => ({ ...prev, criteria: updatedCriteria }))
    }
  }

  const handlePersonAttributesChange = (newAttributes) => {
    setPersonAttributesState(newAttributes)
    setValue('filters.0.notifications', newAttributes, { shouldDirty: true })
    setPersonData((prev) => ({ ...prev, attributes: newAttributes }))
  }

  const handleBusinessAttributesChange = (newAttributes) => {
    setBusinessAttributes(newAttributes)
    setValue('filters.0.notifications', newAttributes, { shouldDirty: true })
    setBusinessData((prev) => ({ ...prev, attributes: newAttributes }))
  }

  const sanitizeCriteriaAmounts = (filters) =>
    filters.map((filter) => ({
      ...filter,
      criteria: (filter.criteria || []).map((criterion) => ({
        ...criterion,
        amount: Number(criterion.amount) > 0 ? Number(criterion.amount) : 1,
      })),
    }))

  const updateNotifications = async (_formData) => {
    const sanitize = (filter) => {
      const { criteria, activeFilter } = filter
      criteria.forEach((c) => {
        if (activeFilter === 'CLIENT_TYPE') {
          delete c.riskLevel
          delete c.fileStatus
        } else if (activeFilter === 'RISK_LEVEL') {
          delete c.clientType
          delete c.fileStatus
        } else if (activeFilter === 'FILE_STATUS') {
          delete c.riskLevel
          delete c.clientType
        }
      })
    }

    // Solo un filtro de business y uno de person
    const allFilters = []

    if (businessData.typeFilter && (businessData.criteria.length > 0 || businessData.attributes.length > 0)) {
      allFilters.push({
        activeFilter: businessData.typeFilter,
        criteria: businessData.criteria,
        type: 'BUSINESS',
        notifications: businessData.attributes,
      })
    }

    if (personData.typeFilter && (personData.criteria.length > 0 || personData.attributes.length > 0)) {
      allFilters.push({
        activeFilter: personData.typeFilter,
        criteria: personData.criteria,
        type: 'PERSON',
        notifications: personData.attributes,
      })
    }

    // SANITIZAR AMOUNTS
    const sanitizedFilters = sanitizeCriteriaAmounts(allFilters)
    sanitizedFilters.forEach(sanitize)

    try {
      await updateKybNotificationsMonitoring({ filters: sanitizedFilters }).unwrap()
      toaster.success(t('messages:updateSuccess'))
      refetch()
    } catch (error) {
      toaster.error(`${t('messages:updateError')}: ${t('messages:contactSupport')}`)
    }
  }

  const createNotifications = async (_formData) => {
    const sanitize = (filter) => {
      const { criteria, activeFilter } = filter
      criteria.forEach((c) => {
        if (activeFilter === 'CLIENT_TYPE') {
          delete c.riskLevel
          delete c.fileStatus
        } else if (activeFilter === 'RISK_LEVEL') {
          delete c.clientType
          delete c.fileStatus
        } else if (activeFilter === 'FILE_STATUS') {
          delete c.riskLevel
          delete c.clientType
        }
      })
    }

    // Solo un filtro de business y uno de person
    const allFilters = []

    if (businessData.typeFilter && (businessData.criteria.length > 0 || businessData.attributes.length > 0)) {
      allFilters.push({
        activeFilter: businessData.typeFilter,
        criteria: businessData.criteria,
        type: 'BUSINESS',
        notifications: businessData.attributes,
      })
    }

    if (personData.typeFilter && (personData.criteria.length > 0 || personData.attributes.length > 0)) {
      allFilters.push({
        activeFilter: personData.typeFilter,
        criteria: personData.criteria,
        type: 'PERSON',
        notifications: personData.attributes,
      })
    }

    // SANITIZAR AMOUNTS
    const sanitizedFilters = sanitizeCriteriaAmounts(allFilters)
    sanitizedFilters.forEach(sanitize)

    try {
      await createKybNotificationsMonitoring({ filters: sanitizedFilters }).unwrap()
      toaster.success(t('messages:creationSuccess'))
      refetch()
    } catch (error) {
      toaster.error(`${t('messages:creationError')}: ${t('messages:contactSupport')}`)
    }
  }

  const handlerTypeFilterChange = (e) => {
    const newTypeFilter = e.target.value
    const previousTypeFilter = getCurrentTypeFilter()

    setCurrentTypeFilter(newTypeFilter)
    setValue('filters.0.activeFilter', newTypeFilter, { shouldDirty: true })

    // Solo resetear criterios si realmente cambió el tipo de filtro
    if (previousTypeFilter !== newTypeFilter) {
      setValue('filters.0.criteria', [], { shouldDirty: true })

      // Actualizar los datos persistentes inmediatamente
      if (entityType === 'business') {
        setBusinessData((prev) => ({ ...prev, typeFilter: newTypeFilter, criteria: [] }))
      } else {
        setPersonData((prev) => ({ ...prev, typeFilter: newTypeFilter, criteria: [] }))
      }
    }
  }

  // Función para sincronizar datos manualmente cuando se cambia de entidad
  const syncDataToState = useCallback(() => {
    const currentFormData = watch('filters.0')
    if (!currentFormData) return

    if (entityType === 'business') {
      setBusinessData((prev) => ({
        ...prev,
        criteria: currentFormData.criteria || [],
        attributes: currentFormData.notifications || [],
      }))
    } else if (entityType === 'person') {
      setPersonData((prev) => ({
        ...prev,
        criteria: currentFormData.criteria || [],
        attributes: currentFormData.notifications || [],
      }))
    }
  }, [watch, entityType])

  // Función para sincronizar datos del estado al formulario
  const syncStateToForm = useCallback(() => {
    if (entityType === 'business') {
      setValue('filters.0.activeFilter', businessData.typeFilter, { shouldDirty: false })
      setValue('filters.0.criteria', businessData.criteria, { shouldDirty: false })
      setValue('filters.0.notifications', businessData.attributes, { shouldDirty: false })
    } else if (entityType === 'person') {
      setValue('filters.0.activeFilter', personData.typeFilter, { shouldDirty: false })
      setValue('filters.0.criteria', personData.criteria, { shouldDirty: false })
      setValue('filters.0.notifications', personData.attributes, { shouldDirty: false })
    }
  }, [entityType, businessData, personData, setValue])

  useEffect(() => {
    // Cargar datos existentes cuando se cargan los datos iniciales
    const loadDataForCurrentEntity = () => {
      if (kybNotificationsMonitoring?.data?.filters) {
        const existingFilters = kybNotificationsMonitoring.data.filters

        // Cargar datos de business
        const businessFilters = existingFilters.filter((f) => f.type === 'BUSINESS')
        if (businessFilters.length > 0) {
          const businessFilter = businessFilters[0]
          setBusinessData({
            typeFilter: businessFilter.activeFilter,
            criteria: businessFilter.criteria || [],
            attributes: businessFilter.notifications || [],
          })
          setBusinessTypeFilter(businessFilter.activeFilter)
          setBusinessAttributes(businessFilter.notifications || [])
        }

        // Cargar datos de person
        const personFilters = existingFilters.filter((f) => f.type === 'PERSON')
        if (personFilters.length > 0) {
          const personFilter = personFilters[0]
          setPersonData({
            typeFilter: personFilter.activeFilter,
            criteria: personFilter.criteria || [],
            attributes: personFilter.notifications || [],
          })
          setPersonTypeFilter(personFilter.activeFilter)
          setPersonAttributesState(personFilter.notifications || [])
        }

        // Cargar datos en el formulario para la entidad actual
        if (entityType === 'business') {
          if (businessFilters.length > 0) {
            const businessFilter = businessFilters[0]
            setValue('filters.0.activeFilter', businessFilter.activeFilter)
            setValue('filters.0.criteria', businessFilter.criteria || [])
            setValue('filters.0.notifications', businessFilter.notifications || [])
          } else {
            setValue('filters.0.activeFilter', '')
            setValue('filters.0.criteria', [])
            setValue('filters.0.notifications', [])
          }
        } else if (entityType === 'person') {
          if (personFilters.length > 0) {
            const personFilter = personFilters[0]
            setValue('filters.0.activeFilter', personFilter.activeFilter)
            setValue('filters.0.criteria', personFilter.criteria || [])
            setValue('filters.0.notifications', personFilter.notifications || [])
          } else {
            setValue('filters.0.activeFilter', '')
            setValue('filters.0.criteria', [])
            setValue('filters.0.notifications', [])
          }
        }
      }
    }

    loadDataForCurrentEntity()
  }, [kybNotificationsMonitoring?.data?.filters, setValue, entityType])

  useEffect(() => {
    // Sincronizar datos del formulario actual al estado antes de cambiar entidad
    syncDataToState()

    // Luego sincronizar el estado de la nueva entidad al formulario
    syncStateToForm()
  }, [entityType, syncDataToState, syncStateToForm])

  // Efecto para sincronizar cambios de amount/unit desde FiltersNotificationsMonitoring
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      // Solo sincronizar si el cambio viene de amount o unit (no de activeFilter, criteria o notifications)
      if (name && (name.includes('amount') || name.includes('unit')) && value.filters?.[0]) {
        const currentFormData = value.filters[0]

        if (entityType === 'business') {
          setBusinessData((prev) => ({
            ...prev,
            criteria: currentFormData.criteria || [],
          }))
        } else if (entityType === 'person') {
          setPersonData((prev) => ({
            ...prev,
            criteria: currentFormData.criteria || [],
          }))
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, entityType])

  // Función para validar si hay datos válidos para guardar
  const hasValidDataToSave = () => {
    const hasBusinessData =
      businessData.typeFilter && (businessData.criteria.length > 0 || businessData.attributes.length > 0)
    const hasPersonData = personData.typeFilter && (personData.criteria.length > 0 || personData.attributes.length > 0)
    return hasBusinessData || hasPersonData
  }

  // Función para validar si los datos han cambiado
  const hasDataChanged = () => {
    if (!kybNotificationsMonitoring?.data?.filters) return true

    const existingFilters = kybNotificationsMonitoring.data.filters
    const existingBusiness = existingFilters.find((f) => f.type === 'BUSINESS')
    const existingPerson = existingFilters.find((f) => f.type === 'PERSON')

    // Verificar cambios en business
    const businessChanged = existingBusiness
      ? existingBusiness.activeFilter !== businessData.typeFilter ||
        JSON.stringify(existingBusiness.criteria) !== JSON.stringify(businessData.criteria) ||
        JSON.stringify(existingBusiness.notifications) !== JSON.stringify(businessData.attributes)
      : businessData.typeFilter !== '' || businessData.criteria.length > 0 || businessData.attributes.length > 0

    // Verificar cambios en person
    const personChanged = existingPerson
      ? existingPerson.activeFilter !== personData.typeFilter ||
        JSON.stringify(existingPerson.criteria) !== JSON.stringify(personData.criteria) ||
        JSON.stringify(existingPerson.notifications) !== JSON.stringify(personData.attributes)
      : personData.typeFilter !== '' || personData.criteria.length > 0 || personData.attributes.length > 0

    return businessChanged || personChanged
  }

  const handleClear = async () => {
    // Volver a traer los datos originales desde la API
    const result = await refetch()
    const filtersFromApi = result?.data?.data?.filters || []
    const newDefaultValues = {
      filters: filtersFromApi.map((filter) => ({
        ...filter,
        criteria: (filter.criteria || []).map((criterion) => ({
          ...criterion,
          amount: Number(criterion.amount) > 0 ? Number(criterion.amount) : 1,
        })),
        notifications: filter.notifications || [],
      })),
    }
    reset(newDefaultValues)

    // Sincronizar los estados locales
    const businessFilter = filtersFromApi.find((f) => f.type === 'BUSINESS')
    setBusinessData({
      typeFilter: businessFilter?.activeFilter || '',
      criteria: (businessFilter?.criteria || []).map((criterion) => ({
        ...criterion,
        amount: Number(criterion.amount) > 0 ? Number(criterion.amount) : 1,
      })),
      attributes: businessFilter?.notifications || [],
    })
    setBusinessTypeFilter(businessFilter?.activeFilter || '')
    setBusinessAttributes(businessFilter?.notifications || [])

    const personFilter = filtersFromApi.find((f) => f.type === 'PERSON')
    setPersonData({
      typeFilter: personFilter?.activeFilter || '',
      criteria: (personFilter?.criteria || []).map((criterion) => ({
        ...criterion,
        amount: Number(criterion.amount) > 0 ? Number(criterion.amount) : 1,
      })),
      attributes: personFilter?.notifications || [],
    })
    setPersonTypeFilter(personFilter?.activeFilter || '')
    setPersonAttributesState(personFilter?.notifications || [])
  }

  if (!getCurrentTypeFilter() && loadingNotificationsMonitoring) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    )
  }

  const onSubmit = kybNotificationsMonitoring?.data ? updateNotifications : createNotifications

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} display="flex" flexDirection="column" gap="2rem">
      <Typography variant="title">{t('common:settings')}</Typography>

      {/* Botones de entidad justo debajo del bloque de criterios */}
      <Box display="flex" gap={2} mb={1}>
        <Chip
          label={t('common:business')}
          clickable
          onClick={() => setEntityType('business')}
          variant={entityType === 'business' ? 'sliderSelected' : 'slider'}
          sx={{
            minWidth: 140,
            borderRadius: '24px',
            fontWeight: 700,
            fontSize: '1rem',
            px: 2,
            py: 1,
            height: 44,
            cursor: 'pointer',
            textTransform: 'none',
          }}
        />
        <Chip
          label={t('common:person')}
          clickable
          onClick={() => setEntityType('person')}
          variant={entityType === 'person' ? 'sliderSelected' : 'slider'}
          sx={{
            minWidth: 140,
            borderRadius: '24px',
            fontWeight: 700,
            fontSize: '1rem',
            px: 2,
            py: 1,
            height: 44,
            cursor: 'pointer',
            textTransform: 'none',
          }}
        />
      </Box>

      {/* Bloque colapsable para Business Data */}
      {entityType === 'business' && (
        <Box mb={2}>
          <Select
            value=""
            displayEmpty
            readOnly
            disableUnderline
            IconComponent={allBusinessData ? ExpandLessIcon : ExpandMoreIcon}
            onClick={() => setAllBusinessData((v) => !v)}
            sx={{
              minWidth: 220,
              maxWidth: 320,
              height: 44,
              fontWeight: 600,
              fontSize: '1rem',
              px: 2,
              py: 1,
              '.MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
              },
            }}
            renderValue={() => (
              <Typography variant="subtitle2" fontWeight={600}>
                {t('messages:kybNotificationsMonitoring.businessDataTitle')}
              </Typography>
            )}
          />
          {allBusinessData && (
            <Box mt={1}>
              {/* Business data group with select all */}
              <Box mb={1}>
                <Box display="flex" alignItems="center" mb={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={businessAttributes.length === BUSINESS_ATTRIBUTES.length}
                        indeterminate={
                          businessAttributes.length > 0 && businessAttributes.length < BUSINESS_ATTRIBUTES.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleBusinessAttributesChange(BUSINESS_ATTRIBUTES.map((o) => o.value))
                          } else {
                            handleBusinessAttributesChange([])
                          }
                        }}
                        color="success"
                        size="small"
                        sx={{ p: 0.5 }}
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={600}>
                        {t('messages:kybNotificationsMonitoring.selectAllBusiness', 'Select all attributes')}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
                <Box display="flex" flexDirection="column">
                  {BUSINESS_ATTRIBUTES.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      control={
                        <Checkbox
                          checked={businessAttributes.includes(opt.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleBusinessAttributesChange([...businessAttributes, opt.value])
                            } else {
                              handleBusinessAttributesChange(businessAttributes.filter((v) => v !== opt.value))
                            }
                          }}
                          color="success"
                          size="small"
                          sx={{ p: 0.5 }}
                        />
                      }
                      label={<Typography variant="body2">{opt.label}</Typography>}
                      sx={{ m: 0 }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Bloque colapsable solo para Data, Checks y Related */}
      {entityType === 'person' && (
        <Box mb={2}>
          <Select
            value=""
            displayEmpty
            readOnly
            disableUnderline
            IconComponent={allPersonData ? ExpandLessIcon : ExpandMoreIcon}
            onClick={() => setAllPersonData((v) => !v)}
            sx={{
              minWidth: 220,
              maxWidth: 320,
              height: 44,
              fontWeight: 600,
              fontSize: '1rem',
              px: 2,
              py: 1,
              '.MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
              },
            }}
            renderValue={() => (
              <Typography variant="subtitle2" fontWeight={600}>
                {t('messages:kybNotificationsMonitoring.personDataTitle')}
              </Typography>
            )}
          />
          {allPersonData && (
            <Box mt={1}>
              {/* Data group with select all */}
              <Box mb={1}>
                <Box display="flex" alignItems="center" mb={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={personAttributesState.length === PERSON_ATTRIBUTES.length}
                        indeterminate={
                          personAttributesState.length > 0 && personAttributesState.length < PERSON_ATTRIBUTES.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            handlePersonAttributesChange(PERSON_ATTRIBUTES.map((o) => o.value))
                          } else {
                            handlePersonAttributesChange([])
                          }
                        }}
                        color="success"
                        size="small"
                        sx={{ p: 0.5 }}
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={600}>
                        {t('messages:kybNotificationsMonitoring.selectAllPerson', 'Select all attributes')}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
                <Box display="flex" flexDirection="column">
                  {PERSON_ATTRIBUTES.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      control={
                        <Checkbox
                          checked={personAttributesState.includes(opt.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handlePersonAttributesChange([...personAttributesState, opt.value])
                            } else {
                              handlePersonAttributesChange(personAttributesState.filter((v) => v !== opt.value))
                            }
                          }}
                          color="success"
                          size="small"
                          sx={{ p: 0.5 }}
                        />
                      }
                      label={<Typography variant="body2">{opt.label}</Typography>}
                      sx={{ m: 0 }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Selector de tipo de filtro siempre visible, sin desplegable */}
      <Box mb={0.5}>
        <Typography variant="subtitle2" fontWeight={400} mb={0.25}>
          {t('messages:select.typeFilter')}
        </Typography>
        <Box>
          <Select
            value={getCurrentTypeFilter() ?? ''}
            onChange={handlerTypeFilterChange}
            size="small"
            sx={{ width: 240, mt: 1 }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              {t('messages:select.typeFilter')}
            </MenuItem>
            <MenuItem value="RISK_LEVEL">{t('common:riskLevel')}</MenuItem>
            <MenuItem value="CLIENT_TYPE">{t('common:clientType')}</MenuItem>
            <MenuItem value="FILE_STATUS">{t('common:status')}</MenuItem>
          </Select>
          {getCurrentTypeFilter() === 'CLIENT_TYPE' && optionsClientTypes.length === 0 && (
            <Box display="flex" flexDirection="column" alignItems="center" mt={0.5} gap={0.5}>
              <Typography variant="body2" color="text.secondary" align="center">
                {t('messages:clientType.isRequired')}
              </Typography>
              <Button
                component={RouterLink}
                to="/kyb/settings"
                variant="contained"
                size="small"
                sx={{ width: 'fit-content' }}
              >
                {t('messages:clientType.settings')}
              </Button>
            </Box>
          )}
          {!getCurrentTypeFilter() && !loadingNotificationsMonitoring && (
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {t('messages:select.newFilterType')}
            </Typography>
          )}
        </Box>
      </Box>

      {getCurrentTypeFilter() && (
        <FiltersNotificationsMonitoring
          filters={filters}
          control={control}
          optionLevels={getCurrentTypeFilter() === 'RISK_LEVEL' ? optionLevels : []}
          optionsClientTypes={getCurrentTypeFilter() === 'CLIENT_TYPE' ? optionsClientTypes : []}
          optionsFileStatus={getCurrentTypeFilter() === 'FILE_STATUS' ? optionsStatus : []}
          optionsUnits={optionsUnits}
          typeFilter={getCurrentTypeFilter()}
          activeFilters={activeFilters}
          handleAddLevel={handleAddLevel}
          handleRemoveCriterion={handleRemoveCriterion}
        />
      )}

      <Box display="flex" justifyContent="flex-end" gap="1rem" mr="1rem">
        <Button
          type="button"
          variant="outlined"
          size="small"
          disabled={loadingUserMaster || !hasDataChanged()}
          onClick={handleClear}
        >
          {t('common:clear')}
        </Button>

        <LoadingButton
          type="submit"
          variant="contained"
          size="small"
          disabled={
            !hasValidDataToSave() ||
            !hasDataChanged() ||
            loadingUserMaster ||
            loadingNotificationsMonitoring ||
            updatingNotificationsMonitoring
          }
        >
          <span>{t('common:save')}</span>
        </LoadingButton>
      </Box>
    </Box>
  )
}

export default KybNotificationsMonitoring
